import styled from "styled-components";
import { Reveal } from "react-awesome-reveal";
import { slideFade } from "../../utils/animations";

const PricingSection = styled.section`
  position: relative;
  padding: 6rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3.5rem;
`;

const Header = styled.div`
  text-align: center;
  max-width: 680px;
`;

const Title = styled.h2`
  font-size: min(3rem, 10vw);
  font-weight: bold;
  color: #333;
  margin: 0 0 1rem 0;
  letter-spacing: -0.02em;
  line-height: 1.15;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #7b746c;
  line-height: 1.65;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  max-width: 1100px;
  width: 100%;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    max-width: 440px;
  }
`;

const PricingCard = styled.div`
  border-radius: 22px;
  padding: 2px;
  background: radial-gradient(circle at top left, #ffe2c8, #ffffff);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.12);
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease;
  display: flex;
  flex-direction: column;
  position: relative;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 32px 72px rgba(0, 0, 0, 0.16);
  }
`;

const CardBody = styled.div`
  border-radius: 20px;
  background: #fffdfc;
  padding: 2.25rem 2rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const PlanName = styled.h3`
  font-size: 1.05rem;
  font-weight: 700;
  color: #231f1a;
  margin: 0 0 0.35rem 0;
  letter-spacing: -0.01em;
`;

const PlanDescription = styled.p`
  font-size: 0.88rem;
  color: #9e948c;
  margin: 0 0 1.75rem 0;
  line-height: 1.5;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.3rem;
  margin-bottom: 0.4rem;
`;

const PriceAmount = styled.span`
  font-size: 3rem;
  font-weight: 800;
  color: #1a1714;
  letter-spacing: -0.03em;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PriceCurrency = styled.span`
  font-size: 1.4rem;
  font-weight: 700;
  color: #7b746c;
  align-self: flex-start;
  margin-top: 0.5rem;
`;

const PriceUnit = styled.span`
  font-size: 0.9rem;
  color: #9e948c;
  font-weight: 500;
`;

const BillingNote = styled.p`
  font-size: 0.78rem;
  color: #b5afa9;
  margin: 0 0 1.75rem 0;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #f0ebe6;
  margin: 0 0 1.5rem 0;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem 0;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FeatureItem = styled.li`
  font-size: 0.88rem;
  color: #4a4540;
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  line-height: 1.45;
`;

const FeatureIcon = styled.span`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
`;

const FeatureText = styled.span`
  flex: 1;
`;

const ComingSoonBadge = styled.span`
  font-size: 0.62rem;
  font-weight: 600;
  color: #eb5e28;
  background: rgba(235, 94, 40, 0.1);
  border-radius: 999px;
  padding: 1px 7px;
  display: inline;
  margin-left: 0.4rem;
  white-space: nowrap;
  vertical-align: middle;
`;

const CTAButton = styled.button`
  width: 100%;
  margin-top: auto;
`;

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="7" fill="rgba(47,158,68,0.12)" />
    <path
      d="M4.5 8L7 10.5L11.5 6"
      stroke="#2f9e44"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SparkleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M8 2L9.3 6.7L14 8L9.3 9.3L8 14L6.7 9.3L2 8L6.7 6.7L8 2Z"
      fill="#f59f00"
      stroke="#f59f00"
      strokeWidth="0.4"
      strokeLinejoin="round"
    />
  </svg>
);

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="6.5" stroke="#c5bfb9" strokeWidth="1.5" />
    <path
      d="M8 5V8.5L10 10"
      stroke="#c5bfb9"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function Pricing({ onAuthOpen }) {
  return (
    <PricingSection id="pricing">
      <Reveal keyframes={slideFade} duration={1500} triggerOnce={true}>
        <Header>
          <Title>Pricing for Teams of All Sizes</Title>
          <Subtitle>
            Start free during beta. No credit card required. Pick a plan that
            fits your hiring needs.
          </Subtitle>
        </Header>
      </Reveal>

      <PricingGrid>
        <Reveal
          keyframes={slideFade}
          duration={1500}
          delay={100}
          triggerOnce={true}
        >
          <PricingCard>
            <CardBody>
              <PlanName>Starter</PlanName>
              <PlanDescription>Perfect for occasional hiring.</PlanDescription>

              <PriceRow>
                <PriceCurrency>$</PriceCurrency>
                <PriceAmount>99</PriceAmount>
                <PriceUnit>/mo</PriceUnit>
              </PriceRow>
              <BillingNote>Billed monthly</BillingNote>

              <Divider />

              <FeatureList>
                <FeatureItem>
                  <FeatureIcon>
                    <CheckIcon />
                  </FeatureIcon>
                  <FeatureText>1 active job per month</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>
                    <CheckIcon />
                  </FeatureIcon>
                  <FeatureText>Up to 30 candidates</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>
                    <SparkleIcon />
                  </FeatureIcon>
                  <FeatureText>AI-powered screening</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>
                    <SparkleIcon />
                  </FeatureIcon>
                  <FeatureText>AI candidate scores &amp; summaries</FeatureText>
                </FeatureItem>
              </FeatureList>

              <CTAButton onClick={onAuthOpen}>Get Free Access</CTAButton>
            </CardBody>
          </PricingCard>
        </Reveal>

        <Reveal
          keyframes={slideFade}
          duration={1500}
          delay={200}
          triggerOnce={true}
        >
          <PricingCard>
            <CardBody>
              <PlanName>Go</PlanName>
              <PlanDescription>
                Best for growing teams with regular hiring.
              </PlanDescription>

              <PriceRow>
                <PriceCurrency>$</PriceCurrency>
                <PriceAmount>149</PriceAmount>
                <PriceUnit>/mo</PriceUnit>
              </PriceRow>
              <BillingNote>Billed monthly</BillingNote>

              <Divider />

              <FeatureList>
                <FeatureItem>
                  <FeatureIcon>
                    <CheckIcon />
                  </FeatureIcon>
                  <FeatureText>Up to 3 active jobs per month</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>
                    <CheckIcon />
                  </FeatureIcon>
                  <FeatureText>Up to 50 candidates per job</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>
                    <SparkleIcon />
                  </FeatureIcon>
                  <FeatureText>AI-powered screening</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>
                    <SparkleIcon />
                  </FeatureIcon>
                  <FeatureText>AI candidate scores &amp; summaries</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>
                    <ClockIcon />
                  </FeatureIcon>
                  <FeatureText>
                    Automated interview scheduling &amp; onboarding routines
                    <ComingSoonBadge>coming soon</ComingSoonBadge>
                  </FeatureText>
                </FeatureItem>
              </FeatureList>

              <CTAButton onClick={onAuthOpen}>Get Free Access</CTAButton>
            </CardBody>
          </PricingCard>
        </Reveal>

        <Reveal
          keyframes={slideFade}
          duration={1500}
          delay={300}
          triggerOnce={true}
        >
          <PricingCard>
            <CardBody>
              <PlanName>Scale</PlanName>
              <PlanDescription>
                For enterprises with high-volume hiring.
              </PlanDescription>

              <PriceRow>
                <PriceCurrency>$</PriceCurrency>
                <PriceAmount>499</PriceAmount>
                <PriceUnit>/mo</PriceUnit>
              </PriceRow>
              <BillingNote>Billed monthly</BillingNote>

              <Divider />

              <FeatureList>
                <FeatureItem>
                  <FeatureIcon>
                    <CheckIcon />
                  </FeatureIcon>
                  <FeatureText>Up to 10 active jobs per month</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>
                    <CheckIcon />
                  </FeatureIcon>
                  <FeatureText>Up to 100 candidates per job</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>
                    <SparkleIcon />
                  </FeatureIcon>
                  <FeatureText>AI-powered screening</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>
                    <SparkleIcon />
                  </FeatureIcon>
                  <FeatureText>AI candidate scores &amp; summaries</FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>
                    <ClockIcon />
                  </FeatureIcon>
                  <FeatureText>
                    Automated interview scheduling &amp; onboarding routines
                    <ComingSoonBadge>coming soon</ComingSoonBadge>
                  </FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>
                    <ClockIcon />
                  </FeatureIcon>
                  <FeatureText>
                    Team collaboration &amp; access control
                    <ComingSoonBadge>coming soon</ComingSoonBadge>
                  </FeatureText>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon>
                    <ClockIcon />
                  </FeatureIcon>
                  <FeatureText>
                    Workforce management tools
                    <ComingSoonBadge>coming soon</ComingSoonBadge>
                  </FeatureText>
                </FeatureItem>
              </FeatureList>

              <CTAButton onClick={onAuthOpen}>Get Free Access</CTAButton>
            </CardBody>
          </PricingCard>
        </Reveal>
      </PricingGrid>
    </PricingSection>
  );
}

export default Pricing;
